import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Property } from '../../../shared/types/Property';
import { BuildingIcon, TagIcon, MapPinIcon, PhoneIcon, GlobeIcon, ClockIcon, CheckIcon, XIcon, InfoIcon } from 'lucide-react';
import { InfoTooltip } from '../../../shared/ui/InfoTooltip';
export function PropertyInformation() {
  const {
    t
  } = useTranslation('rooms');
  // Mock data for a Malaysian island resort
  const [property, setProperty] = useState<Property>({
    name: 'Baa Atoll Lagoon Paradise Resort',
    description: 'Nestled on the pristine shores of Langkawi, Langkawi Lagoon Paradise Resort offers a perfect blend of Malaysian luxury and natural beauty. Surrounded by turquoise lagoons and overlooking the crystal-clear waters of the Andaman Sea, this exclusive retreat provides an authentic Malaysian experience with modern comforts.',
    designation: 'Resort',
    starRating: 5,
    tags: ['Overwater Villas', 'Spa Retreat', 'Family-Friendly', 'Luxury', 'Malaysian Cuisine', 'Beachfront', 'Island Escape', 'Wellness-Retreat', 'All-Inclusive', 'Yoga Classes', 'Eco-Friendly', 'Cultural Experiences'],
    address: {
      office: 'Eydhafushi Island',
      city: 'Baa Atoll',
      postalCode: '06010',
      country: 'Malaysia',
      latitude: '5.1042',
      longitude: '73.0680'
    },
    contact: {
      email: 'reservations@baaatolllagoon.mv',
      phone: '+960 660 1234',
      website: 'https://www.baaatolllagoonresort.mv',
      timezone: 'Asia/Kuala_Lumpur'
    },
    checkIn: '14:00',
    checkOut: '12:00',
    amenities: ['Infinity Pool', 'Private Pool Villas', 'WiFi', 'Airport Shuttle', 'Daily Housekeeping', 'Smart TV', 'Hair Dryer', 'In-Room Safe', 'Bicycle Rental', 'Malaysian Cooking Classes', 'Sunset Cruise', 'Island Hopping', 'Floating Breakfast', 'Traditional Malaysian Massage', 'Bodu Beru Performances', 'Dolphin Cruise Tours'],
    transportation: ['Taxi', 'Hotel Shuttle', 'Motorbike Rental', 'Longtail Boat', 'Ferry', 'Domestic Flight', 'Private Van Transfer']
  });
  // Function to render tags
  const renderTags = (tags: string[]) => {
    return <div className="flex flex-wrap gap-2" data-id="element-10269">
        {tags.map((tag, index) => <div key={index} className="flex items-center bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded" data-id="element-10270">
            {tag}
            <button aria-label={`Remove ${tag}`} className="ml-1 text-gray-500 hover:text-gray-700" data-id="element-10271">
              <XIcon size={12} aria-hidden="true" data-id="element-10272" />
            </button>
          </div>)}
      </div>;
  };
  // Draft information tooltip content
  const draftInfoContent = "Your hotel listing is currently in draft mode. It won't be visible until your booking engine is enabled. Once you add availability or offers through your inventory management, our team will review and publish them when ready.";
  return <div className="space-y-6" data-id="element-10273">
      {/* Simplified header section */}
      <div data-id="element-10274">
        <h1 className="text-2xl font-semibold text-gray-800 mb-4" data-id="element-10275">
          {t('pages.PropertyInformation.hotel')}
        </h1>
        <div className="flex justify-between items-center" data-id="element-10276">
          {/* Left side - tabs */}
          <div className="border-b border-gray-200" data-id="element-10277">
            <div className="flex space-x-6" data-id="element-10278">
              <button className="text-sm font-medium text-blue-600 border-b-2 border-blue-600 pb-2" data-id="element-10279">
                {t('pages.PropertyInformation.hotel_information')}
              </button>
              <button className="text-sm font-medium text-gray-500 hover:text-gray-800 pb-2" data-id="element-10280">
                {t('components.EditRoomForm.media_assets')}
              </button>
            </div>
          </div>
          {/* Right side - status button with tooltip */}
          <div className="relative group" data-id="element-10281">
            <button className="flex items-center bg-gray-200 text-gray-700 px-4 py-2 rounded-md text-sm font-medium" data-id="element-10282">
              <span className="mr-2" data-id="element-10283">
                {t('pages.PropertyInformation.draft_submitted_for_review')}
              </span>
              <InfoIcon size={16} className="text-gray-500" data-id="element-10284" />
            </button>
            <div className="absolute right-0 mt-2 w-72 p-3 bg-white border border-gray-200 rounded-lg shadow-lg z-10 hidden group-hover:block" data-id="element-10285">
              <h3 className="text-sm font-medium text-gray-800 mb-1" data-id="element-10286">
                {t('pages.PropertyInformation.draft_status')}
              </h3>
              <p className="text-xs text-gray-600" data-id="element-10287">{draftInfoContent}</p>
            </div>
          </div>
        </div>
      </div>
      {/* Hotel Details Form */}
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-6" data-id="element-10288">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-10289">
          <div data-id="element-10290">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10291">
              {t('pages.PropertyInformation.hotel_name')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.name} onChange={e => setProperty({
            ...property,
            name: e.target.value
          })} data-id="element-10292" />
          </div>
          <div data-id="element-10293">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10294">
              {t('pages.PropertyInformation.designation')}
            </label>
            <div className="relative" data-id="element-10295">
              <select className="w-full border border-gray-300 rounded-md px-3 py-2 appearance-none" value={property.designation} onChange={e => setProperty({
              ...property,
              designation: e.target.value
            })} data-id="element-10296">
                <option value="Resort" data-id="element-10297">
                  {t('pages.PropertyInformation.resort')}
                </option>
                <option value="Hotel" data-id="element-10298">
                  {t('pages.PropertyInformation.hotel')}
                </option>
                <option value="Villa" data-id="element-10299">
                  {t('pages.PropertyInformation.villa')}
                </option>
                <option value="Guesthouse" data-id="element-10300">
                  {t('pages.PropertyInformation.guesthouse')}
                </option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-10301">
                <ChevronDownIcon size={16} className="text-gray-400" data-id="element-10302" />
              </div>
            </div>
          </div>
        </div>
        <div className="mb-6" data-id="element-10303">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10304">
            {t('components.AddRoomForm.description')}
          </label>
          <textarea className="w-full border border-gray-300 rounded-md px-3 py-2 h-24" value={property.description} onChange={e => setProperty({
          ...property,
          description: e.target.value
        })} data-id="element-10305" />
        </div>
        <div className="mb-6" data-id="element-10306">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10307">
            {t('pages.PropertyInformation.star_rating')}
          </label>
          <div className="relative" data-id="element-10308">
            <select className="w-full border border-gray-300 rounded-md px-3 py-2 appearance-none" value={property.starRating} onChange={e => setProperty({
            ...property,
            starRating: parseInt(e.target.value)
          })} data-id="element-10309">
              <option value="5" data-id="element-10310">5</option>
              <option value="4" data-id="element-10311">4</option>
              <option value="3" data-id="element-10312">3</option>
              <option value="2" data-id="element-10313">2</option>
              <option value="1" data-id="element-10314">1</option>
            </select>
            <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-10315">
              <ChevronDownIcon size={16} className="text-gray-400" data-id="element-10316" />
            </div>
          </div>
        </div>
        <div className="mb-6" data-id="element-10317">
          <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10318">
            {t('pages.PropertyInformation.tags')}
          </label>
          {renderTags(property.tags)}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-10319">
          <div data-id="element-10320">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10321">
              {t('pages.PropertyInformation.address')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.address.office} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              office: e.target.value
            }
          })} data-id="element-10322" />
          </div>
          <div data-id="element-10323">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10324">
              {t('pages.PropertyInformation.area_name')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value="Pantai Cenang" data-id="element-10325" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-10326">
          <div data-id="element-10327">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10328">
              {t('pages.PropertyInformation.city')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.address.city} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              city: e.target.value
            }
          })} data-id="element-10329" />
          </div>
          <div data-id="element-10330">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10331">
              {t('pages.PropertyInformation.postal_code')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.address.postalCode} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              postalCode: e.target.value
            }
          })} data-id="element-10332" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6" data-id="element-10333">
          <div data-id="element-10334">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10335">
              {t('pages.PropertyInformation.country')}
            </label>
            <div className="relative" data-id="element-10336">
              <select className="w-full border border-gray-300 rounded-md px-3 py-2 appearance-none" value={property.address.country} onChange={e => setProperty({
              ...property,
              address: {
                ...property.address,
                country: e.target.value
              }
            })} data-id="element-10337">
                <option value="Malaysia" data-id="element-10338">
                  {t('pages.PropertyInformation.malaysia')}
                </option>
                <option value="Sri Lanka" data-id="element-10339">
                  {t('pages.PropertyInformation.indonesia')}
                </option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-10340">
                <ChevronDownIcon size={16} className="text-gray-400" data-id="element-10341" />
              </div>
            </div>
          </div>
          <div data-id="element-10342">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10343">
              {t('pages.PropertyInformation.latitude')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.address.latitude} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              latitude: e.target.value
            }
          })} data-id="element-10344" />
          </div>
          <div data-id="element-10345">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10346">
              {t('pages.PropertyInformation.longitude')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.address.longitude} onChange={e => setProperty({
            ...property,
            address: {
              ...property.address,
              longitude: e.target.value
            }
          })} data-id="element-10347" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-10348">
          <div data-id="element-10349">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10350">
              {t('pages.PropertyInformation.contact_email')}
            </label>
            <input type="email" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.contact.email} onChange={e => setProperty({
            ...property,
            contact: {
              ...property.contact,
              email: e.target.value
            }
          })} data-id="element-10351" />
          </div>
          <div data-id="element-10352">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10353">
              {t('pages.PropertyInformation.phone_number')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2 mb-3" value={property.contact.phone} onChange={e => setProperty({
            ...property,
            contact: {
              ...property.contact,
              phone: e.target.value
            }
          })} data-id="element-10354" />
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6" data-id="element-10355">
          <div data-id="element-10356">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10357">
              {t('pages.PropertyInformation.website')}
            </label>
            <input type="text" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.contact.website} onChange={e => setProperty({
            ...property,
            contact: {
              ...property.contact,
              website: e.target.value
            }
          })} data-id="element-10358" />
          </div>
          <div data-id="element-10359">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10360">
              {t('pages.PropertyInformation.timezone')}
            </label>
            <div className="relative" data-id="element-10361">
              <select className="w-full border border-gray-300 rounded-md px-3 py-2 appearance-none" value={property.contact.timezone} onChange={e => setProperty({
              ...property,
              contact: {
                ...property.contact,
                timezone: e.target.value
              }
            })} data-id="element-10362">
                <option value="Asia/Kuala_Lumpur" data-id="element-10363">
                  {t('pages.PropertyInformation.asiakualalumpur')}
                </option>
                <option value="Asia/Singapore" data-id="element-10364">
                  {t('pages.PropertyInformation.asiasingapore')}
                </option>
              </select>
              <div className="absolute inset-y-0 right-0 flex items-center pr-2 pointer-events-none" data-id="element-10365">
                <ChevronDownIcon size={16} className="text-gray-400" data-id="element-10366" />
              </div>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-10367">
          <div data-id="element-10368">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10369">
              {t('pages.PropertyInformation.checkin_time')}
            </label>
            <input type="time" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.checkIn} onChange={e => setProperty({
            ...property,
            checkIn: e.target.value
          })} data-id="element-10370" />
          </div>
          <div data-id="element-10371">
            <label className="block text-sm font-medium text-gray-700 mb-1" data-id="element-10372">
              {t('pages.PropertyInformation.checkout_time')}
            </label>
            <input type="time" className="w-full border border-gray-300 rounded-md px-3 py-2" value={property.checkOut} onChange={e => setProperty({
            ...property,
            checkOut: e.target.value
          })} data-id="element-10373" />
          </div>
        </div>
        <div className="mb-6" data-id="element-10374">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-10375">
            {t('pages.PropertyInformation.hotel_amenities')}
          </label>
          <div className="border border-gray-200 rounded-lg p-4" data-id="element-10376">
            <div className="flex flex-wrap gap-2" data-id="element-10377">
              {property.amenities.map((amenity, index) => <div key={index} className="flex items-center bg-gray-100 text-gray-700 text-xs px-2 py-1 rounded" data-id="element-10378">
                  {amenity}
                  <button aria-label={`Remove ${amenity}`} className="ml-1 text-gray-500 hover:text-gray-700" data-id="element-10379">
                    <XIcon size={12} aria-hidden="true" data-id="element-10380" />
                  </button>
                </div>)}
            </div>
          </div>
        </div>
        <div data-id="element-10381">
          <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-10382">
            {t('pages.PropertyInformation.transportation')}
          </label>
          <div className="space-y-2" data-id="element-10383">
            {property.transportation.map((item, index) => <div key={index} className="flex items-center" data-id="element-10384">
                <input type="checkbox" id={`transportation-${index}`} checked={true} className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" data-id="element-10385" />

                <label htmlFor={`transportation-${index}`} className="ml-2 text-sm text-gray-700" data-id="element-10386">
                  {item}
                </label>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}
// Helper component for the ChevronDown icon
function ChevronDownIcon({
  size = 24,
  className = ''
}) {
  return <svg xmlns="http://www.w3.org/2000/svg" width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className} data-id="element-10387">
      <polyline points="6 9 12 15 18 9" data-id="element-10388"></polyline>
    </svg>;
}